import StaticScreenPositions from "./StaticScreenPositions";
import { CONSTANTS } from "../config/Constants";
import MusicPlayTimes from "../data/MusicPlayTimes";

const romBase = 0x013;

const bitToMask = [
    0x01,
    0x02,
    0x04,
    0x08,
    0x10,
    0x20,
    0x40,
    0x80
];

const RomData = {
    romUrl: CONSTANTS.RomUrl,
    testMessageText: 'PATCH TARGET 1.03',
    testMessagePosition: romBase,
};

function gameLevelDataFormat(data, game) {
    let realData = [];

    data.forEach((level, idx) => {
        // Level is 12x10, so 120 raw bytes of data. We fit 8 tiles into 5 bytes. Put the first 5 tiles into
        // the first 5 bits, then divide the 3*5=15 remaining bits into 15 tiles. 
        // A level will thus be (120/8) = 15 * 5 = 75 bytes, plus style and position, border bytes = 78. 
        // Not great for accessing things quickly, but oh well.
        for (let byteChunk = 0; byteChunk < 15; byteChunk++) {
            let thisChunk = [0, 0, 0, 0, 0];
            const firstByte = byteChunk * 8;
            for (let i = 0; i < 5; i++) {
                thisChunk[i] = level.tiles[firstByte + i];
            }
            for (let i = 0; i < 15; i++) {
                const chunkId = i % 5,
                    bitId = chunkId,
                    byteId = Math.floor(i / 5),
                    targetBitId = byteId,
                    bitFlipped = ((level.tiles[firstByte + 5 + byteId]) & bitToMask[bitId]) ? 1 : 0,
                    bitValue = bitFlipped << (5 + targetBitId)
                thisChunk[chunkId] += bitValue
            }
            realData.push(...thisChunk)
        }
        realData.push(level.borderTile);
        // Grab the default game's gamestyle if a gamestyle isn't written into the current level
        realData.push(level.style === null ? game.defaultLevelStyle : level.style);
        realData.push(level.startPosition);
    });

    return realData;
}

function tileDataPaletteFormat(vals) {
    return vals.map(a => a.palette);
}

function tileDataCollisionFormat(vals) {
    return vals.map(a => a.collision);
}

function formatScreens(vals, game) {
    let data = [];
    let i = 0;
    for (; i < vals.length; i++) {
        vals[i].data.forEach(d => data.push(d));
    }
    for (; i < 14; i++) {
        for (let j = 0; j < 1024; j++) {
            data.push(0);
        }
    }
    return data;
}

function formatScreenTypes(vals) {
    let screenTypes = [];
    console.info('mobx is about to show some warnings about out of bounds index reads, they are safe to ignore!');
    for (let i = 0; i < 16; i++) {
        let value = vals[i]?.showAt ?? 255;
        if (value === StaticScreenPositions.AfterLevel) {
            value += vals[i].showAtDetail;
        } else if (value === StaticScreenPositions.AfterScreen) {
            value += vals.findIndex(a => a.id === vals[i].showAtDetail);
        }
        screenTypes.push(value);
    }
    return screenTypes;
}

function getValForSongId(game, id) {
    if (game.creditsSong === id) {
        return MusicPlayTimes.OnEndScreen;
    } else if (game.gameplaySong === id) {
        return MusicPlayTimes.OnLevel;
    } else if (game.titleSong === id) {
        return MusicPlayTimes.OnTitle;
    } else {
        return 255;
    }
}

function customSongShowTimesFormatter(vals, game) {
    if (game.usingBuiltInMusic) {
        let musicData = [];
        for (let i = 0; i < 16; i++) {
            musicData.push(getValForSongId(game, i));
        }
        return musicData;
    } else {
        let musicData = [];
        for (let i = 0; i < 16; i++) {
            let value = vals[i]?.showAt ?? 255;
            if (value === MusicPlayTimes.OnLevel) {
                value += vals[i].showAtDetail;
            } else if (value === MusicPlayTimes.OnScreen) { 
                value += game.staticScreens.findIndex(s => s.id === vals[i].showAtDetail);
            }
            musicData.push(value);
        }
        return musicData;
    }
}

function customSongIdFormatter(vals, game) {
    if (game.usingBuiltInMusic) {
        return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
    }
    let songIds = [];
    for (let i = 0; i < 16; i++) {
        let value = vals[i]?.songId ?? i;
        songIds.push(value);
    }

    return songIds;
}

RomData.patches = [
    // +1 because of the \n at the end of a .asciiz
    {name: 'title', symbol: '_gameName', address: romBase + RomData.testMessageText.length + 1, length: 29, padText: true, padBinary: false},
    {name: 'lastLevel', symbol: '_totalGameLevels', length: false, dataFormatter: a => a+1},
    {name: 'levels', symbol: '_gameLevelData', length: 4992, dataFormatter: gameLevelDataFormat, comment: "This contains all of your levels in order. Each level comprises of 78 bytes - 75 bytes of map information, followed by 3 bytes: the border tile id, the gameplay mode, and the position the player starts in. The top nybble (4 bits) is the y position tile id, and the bottom nybble is the x position tile id."},
    {name: 'tileData', symbol: '_tileCollisionTypes', length: 32, dataFormatter: tileDataCollisionFormat},
    {name: 'tileData', symbol: '_tilePalettes', length: 32, dataFormatter: tileDataPaletteFormat},
    {name: 'titleScreenData', symbol: '_titleScreenData', length: 0x400},
    {name: 'creditsScreenData', symbol: '_creditsScreenData', length: 0x400},
    {name: 'gamePaletteData', symbol: '_gamePaletteData', length: 16},
    {name: 'spritePalette', symbol: '_spritePalette', length: 4},
    {name: 'unusedByte', symbol: '_unusedByte1', length: false, dataFormatter: () => 255},
    {name: 'singleLevelOverride', symbol: '_singleLevelOverride', length: false, dataFormatter: () => 255},
    {name: 'unusedByte', symbol: '_unusedByte2', length: false, dataFormatter: () => 255},
    {name: 'unusedByte', symbol: '_unusedByte3', length: false, dataFormatter: () => 255},
    {name: 'disableStatsScreen', symbol: '_disableStatsScreen', length: false},
    {name: 'playerSpeed', symbol: '_movementSpeed', length: false},
    {name: 'coinsCollectedText', symbol: '_coinsCollectedText', length: 20},
    {name: 'cratesRemovedText', symbol: '_cratesRemovedText', length: 20},
    {name: 'undoEnabled', symbol: '_enableUndo', length: false},
    {name: 'showLevelEnabled', symbol: '_enableLevelShow', length: false},
    {name: 'showKeyCount', symbol: '_enableKeyCount', length: false},
    {name: 'showGameTitle', symbol: '_showGameTitle', length: false},
    {name: 'showGoal', symbol: '_showGoal', length: false},
    {name: 'animateBlockMovement', symbol: '_animateBlockMovement', length: false},
    {name: 'showPasswords', symbol: '_enablePassword', length: false},
    {name: 'hudData', symbol: '_hudData', length: 192},
    {name: 'hudData', symbol: '_hudDataAttrs', length: 16, dataFormatter: d => d.slice(0x3c0, 0x3d0)},
    {name: 'customSongUsage', symbol: '_customSongShowTimes', length: 16, dataFormatter: customSongShowTimesFormatter},
    {name: 'customSongUsage', symbol: '_customSongIds', length: 16, dataFormatter: customSongIdFormatter},

    {name: 'gameGraphicsTiles', symbol: '', excludeSymbol: true, address: 0x8013, length: 2048},
    {name: 'gameGraphicsFont', symbol: '', excludeSymbol: true, length: 2048},
    {name: 'gameGraphicsTiles', symbol: '', excludeSymbol: true, length: 2048},
    {name: 'gameGraphicsSprites', symbol: '', excludeSymbol: true, length: 2048},
    {name: 'soundtrackData', symbol: '', excludeSymbol: true, address: 0x10013, length: 10240, padBinary: true},
    {name: 'sfxData', symbol: '', excludeSymbol: true, address: 0x1DE97, length: 1024, padBinary: true},
    {name: 'staticScreens', symbol: '_staticScreenTypes', address: 0x14013, length: 16, swapBank: 'STATIC_SC', dataFormatter: formatScreenTypes},
    {name: 'staticScreens', symbol: '_staticScreens', address: 0x14023, length: 1024*14, padBinary: true, dataFormatter: formatScreens, comment: "There are currently 14 static screens available, each of which is given 1kb (1024 bytes) "}

];

RomData.patches.forEach((v, idx, array) => {
    if (v.address) {
        return;
    }
    const pastRes = array[idx - 1],
        len = pastRes.length === false ? 1 : pastRes.length;

    v.address = pastRes.address + len;
});
export default RomData;